import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from './SocketContext';

const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 2 * 60 * 1000;

// Short two-tone chime via Web Audio — no audio asset to ship/load.
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    [880, 1174.66].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.09);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.09 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.09 + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.09);
      osc.stop(now + i * 0.09 + 0.2);
    });
    setTimeout(() => ctx.close(), 500);
  } catch { /* ignore — sound is a nice-to-have */ }
}

export function NotificationProvider({ user, children }) {
  const { socket } = useSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(false);

  const prefs = user?.notificationPrefs || { realtime: true, sound: true };

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/summary', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCount(data.unreadCount || 0);
      setItems(data.items || []);
    } catch { /* ignore */ }
  }, []);

  // Loads a page for the /notifications feed (category filter optional).
  // Resets to page 1 on a new category, otherwise appends the next page.
  const fetchMore = useCallback(async ({ category = 'all', reset = false } = {}) => {
    const page = reset ? 1 : pageRef.current + 1;
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (category !== 'all') params.set('category', category);
      const res = await fetch(`/api/notifications?${params}`, { credentials: 'include' });
      if (!res.ok) return { notifications: [], hasMore: false };
      const data = await res.json();
      pageRef.current = data.page || page;
      setHasMore(!!data.hasMore);
      return data;
    } catch {
      return { notifications: [], hasMore: false };
    }
  }, []);

  const markRead = useCallback(async (id) => {
    setItems(prev => prev.map(n => (n._id === id ? { ...n, read: true } : n)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`/api/notifications/${id}/read`, { method: 'PATCH', credentials: 'include' });
    } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await fetch('/api/notifications/read-all', { method: 'PATCH', credentials: 'include' });
    } catch { /* ignore */ }
  }, []);

  // Removes it from the bell dropdown's `items` too (not just whatever list
  // called this — /notifications and the bell read from the same summary),
  // and only decrements unreadCount when the deleted one was actually unread,
  // so a read item's deletion doesn't push the badge negative.
  const deleteNotification = useCallback(async (id) => {
    let wasUnread = false;
    setItems(prev => {
      const target = prev.find(n => n._id === id);
      wasUnread = !!target && !target.read;
      return prev.filter(n => n._id !== id);
    });
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`/api/notifications/${id}`, { method: 'DELETE', credentials: 'include' });
    } catch { /* ignore */ }
  }, []);

  // Initial load + slow poll fallback (catches anything a missed/dropped
  // socket event didn't deliver).
  useEffect(() => {
    if (!user) { setItems([]); setUnreadCount(0); return undefined; }
    fetchSummary();
    const interval = setInterval(fetchSummary, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, fetchSummary]);

  // Live push — disabled when the user turns off "Real-time notifications",
  // in which case the poll above is the only source of updates.
  useEffect(() => {
    if (!socket || !user || prefs.realtime === false) return undefined;

    function onNew(doc) {
      setItems(prev => [doc, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
      if (prefs.sound !== false) playChime();
    }
    function onReconnect() { fetchSummary(); }

    socket.on('notification:new', onNew);
    socket.on('connect', onReconnect);

    return () => {
      socket.off('notification:new', onNew);
      socket.off('connect', onReconnect);
    };
  }, [socket, user, prefs.realtime, prefs.sound, fetchSummary]);

  return (
    <NotificationContext.Provider value={{ unreadCount, items, hasMore, fetchMore, markRead, markAllRead, deleteNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
